import _ from 'lodash';
import React, { useCallback, useMemo, useRef } from 'react';
import { FieldProps } from 'formik';

import FormControl from '@material-ui/core/FormControl';

import { FormHelperText } from '../../_core/_ui/forms.component';
import { FormikFieldLabel } from '../../_core/Formik/formik-field-label.component';
import { useFormikField } from '../../_core/Formik/use-formik-field';
import { IAdHocTokenReport } from '../../../lib/types/communication.types';
import { segmentTokenIssues } from './compose-panel.token-highlight';
import {
  HighlightBackdrop,
  HighlightInput,
  HighlightTextArea,
  HighlightWrapper,
  TokenMark
} from './token-highlight-field.styles';

interface ITokenHighlightFieldProps extends FieldProps<string> {
  fid?: string;
  id?: string;
  label?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  onFocus?: () => void;
  // Latest server token report; flags unknown / missing-fallback occurrences.
  report?: IAdHocTokenReport;
}

// A Formik text field (input or textarea) with a highlight backdrop that marks
// invalid variable tokens inline, mirroring the preview's mark colours.
export const TokenHighlightField: React.FC<ITokenHighlightFieldProps> = ({
  field,
  form,
  fid,
  id,
  label,
  placeholder,
  disabled,
  multiline,
  rows,
  maxLength,
  onFocus,
  report
}) => {
  const { id: _id, error, touched } = useFormikField({ field, form, fid, id });
  const backdropRef = useRef<HTMLDivElement>(null);

  const value = field.value || '';
  const segments = useMemo(() => segmentTokenIssues(value, report), [value, report]);

  // Lock the backdrop's scroll offset to the field so the marks stay behind
  // their characters as the field scrolls.
  const syncScroll = useCallback((event: React.UIEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const el = event.currentTarget;
    if (backdropRef.current) {
      backdropRef.current.scrollTop = el.scrollTop;
      backdropRef.current.scrollLeft = el.scrollLeft;
    }
  }, []);

  const shared = {
    id: _id,
    name: field.name,
    value,
    onChange: field.onChange,
    onBlur: field.onBlur,
    onFocus,
    onScroll: syncScroll,
    placeholder,
    disabled,
    spellCheck: true
  };

  return (
    <FormControl margin={multiline ? 'dense' : 'normal'} fullWidth component="div" error={!!error && touched}>
      <FormikFieldLabel label={label} htmlFor={_id} />
      <HighlightWrapper $disabled={disabled}>
        <HighlightBackdrop ref={backdropRef} $multiline={multiline} aria-hidden>
          {segments.map((seg, i) => (
            seg.issue
              ? <TokenMark key={i} data-issue={seg.issue}>{seg.text}</TokenMark>
              : <React.Fragment key={i}>{seg.text}</React.Fragment>
          ))}
        </HighlightBackdrop>
        {multiline
          ? <HighlightTextArea {...shared} rows={rows} maxLength={maxLength} />
          : <HighlightInput {...shared} type="text" />}
      </HighlightWrapper>
      {touched && _.isString(error) && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};
